/**
 * Carrier MCP — tool annotation hints.
 *
 * WHY THIS EXISTS
 * ---------------
 * MCP tool annotations (`readOnlyHint`, `destructiveHint`, `idempotentHint`,
 * `openWorldHint`) are the machine-readable half of a tool's safety contract.
 * Carrier already enforces the human half at call time — `wrapHandler` checks
 * scope, and the money-moving tools sit behind a HARD_BLOCK confirm_token — but
 * a client cannot read either of those. Without annotations an agent has no way
 * to know that `stripe_connect_refund` moves real money and `list_subscribers`
 * does not, so it cannot ask for confirmation at the right moments.
 *
 * HOW THE DEFAULTS WORK
 * ---------------------
 * Almost every hint follows from the scope a tool already declares in its
 * module's `*_TOOL_SCOPES` map (aggregated by `tool-registry.ts`):
 *
 *   read   → reads OCS/Stripe state, changes nothing
 *   write  → mutates tenant state, but the change is additive or reversible
 *   admin  → mutates state in a way a caller cannot simply undo
 *
 * `openWorldHint` is `true` for every Carrier tool without exception: they all
 * reach an external system (the eSIMVault/Bridge4IP OCS, Stripe, or a browser
 * agent), so results are never a closed function of the arguments.
 *
 * WHERE THE DEFAULTS ARE WRONG
 * ----------------------------
 * Scope answers "who may call this", not "what happens if it goes wrong". A
 * `write`-scoped delete is still destructive. Those cases are listed explicitly
 * in `ANNOTATION_OVERRIDES` with a reason each, or passed per-call via
 * `annotationsFor(name, scope, overrides)`. Never infer a destructive hint —
 * write it down.
 */

import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolScope } from "./types.js";
import { recordToolDescription } from "@carrier/ocs-client";

/** The four hints the MCP spec defines. All optional; we always set at least three. */
export interface ToolAnnotations {
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
  title?: string;
}

/**
 * Scope-derived defaults.
 *
 * `destructiveHint` is deliberately `false` for `write`: the common write here
 * is an additive one (assign a package, top up a balance, set a contact field).
 * Writes that destroy something are named in ANNOTATION_OVERRIDES.
 */
export function annotationsForScope(scope: ToolScope): ToolAnnotations {
  switch (scope) {
    case "read":
      return {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      };
    case "write":
      return {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: true,
      };
    case "admin":
      return {
        readOnlyHint: false,
        destructiveHint: true,
        idempotentHint: false,
        openWorldHint: true,
      };
  }
}

/**
 * Tools whose scope default is wrong, with the reason.
 *
 * Keep this list short and keep every entry justified. If an entry needs a
 * paragraph to explain, the tool probably wants a different scope instead.
 */
export const ANNOTATION_OVERRIDES: Record<string, ToolAnnotations> = {
  // --- Destructive despite a non-admin scope -------------------------------

  /** Removes a package from a live SIM; the subscriber loses service on it. */
  delete_subscriber_package: { destructiveHint: true },

  /** Can suspend or terminate a line — service stops until reversed. */
  modify_subscriber_status: { destructiveHint: true },

  /** Invalidates the credentials in use; anything holding the old ones breaks. */
  rotate_credentials: { destructiveHint: true },

  /** Moves money back to a cardholder. Not reversible from Carrier's side. */
  stripe_connect_refund: { destructiveHint: true },

  /** Closes a Radar review against the payment; the decision sticks. */
  radar_review_decline: { destructiveHint: true },

  /** Blocks or allows a value fleet-wide; affects future charges, not just one. */
  radar_value_list_add: { destructiveHint: true },

  /** Re-parents a whole subscriber range to another account. */
  move_subscriber_range_to_account: { destructiveHint: true },

  /** Drops entries off a whitelist, so traffic that worked stops working. */
  greenzone_whitelist_remove: { destructiveHint: true },

  /**
   * Charges the saved card off-session with no browser step and no human
   * confirmation. Spending real money irreversibly is the thing an agent most
   * needs warning about, whatever the scope says.
   */
  wallet_auto_topup: { destructiveHint: true },

  // --- Idempotency corrections ---------------------------------------------

  /**
   * 'adapt' mode is additive, so calling twice charges twice. Explicitly not
   * idempotent even though 'set' mode alone would be.
   */
  modify_subscriber_balance: { idempotentHint: false },

  /** Same adapt/set split as the subscriber variant. */
  modify_account_balance: { idempotentHint: false },

  /** Starts a checkout session; each call creates a new one. */
  wallet_topup_checkout: { idempotentHint: false },

  // --- Read-only tools that are not idempotent -----------------------------

  /**
   * Approving a review is an admin state change, but it releases a payment
   * rather than destroying anything.
   */
  radar_review_approve: { destructiveHint: false },

  /**
   * Admin-scoped but a documented no-op: Stripe exposes no API for Radar rule
   * state, so this returns Dashboard instructions and changes nothing. Treated
   * as read-only so an agent does not gate it behind a confirmation it does
   * not need — and so it never reports the rule as toggled.
   */
  radar_rule_toggle: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
  },
};

/**
 * Resolve the annotations for one tool: scope defaults, then the static
 * override table, then any per-call override.
 *
 * The per-call argument exists for hints that are a property of the
 * registration site rather than the tool's identity (a generator that is not
 * idempotent, say). Prefer ANNOTATION_OVERRIDES when the reason is about the
 * tool itself, so the reason lives in one place.
 */
export function annotationsFor(
  name: string,
  scope: ToolScope,
  overrides: ToolAnnotations = {},
): ToolAnnotations {
  return {
    ...annotationsForScope(scope),
    ...(ANNOTATION_OVERRIDES[name] ?? {}),
    ...overrides,
  };
}

/**
 * Is this tool destructive, by any of the definitions the server holds?
 *
 * There were two, and they disagreed on the tools that matter most:
 *
 *   - `DESTRUCTIVE_TOOLS` (tools.ts) is really the OCS *dry-run* list.
 *     Membership means `wrapHandler` can offer a dry_run preview. It contains
 *     no Stripe, wallet or Radar tool, because none of those has a dry-run
 *     path — they are guarded by HARD_BLOCK confirm tokens instead.
 *   - `ANNOTATION_OVERRIDES` here marks what actually cannot be undone.
 *
 * `carrier_ask_describe` reported the first one as its `destructive` field, so
 * asking the server whether `stripe_connect_refund` is destructive returned
 * `false`. Callers get the union now, which is the answer that is true under
 * both definitions.
 *
 * Deliberately NOT merged into `DESTRUCTIVE_TOOLS` itself: that set drives
 * dry-run behaviour, and adding tools with no dry-run path to it would promise
 * a preview that cannot be delivered.
 */
export function isDestructiveTool(
  name: string,
  dryRunSet?: ReadonlySet<string>,
): boolean {
  if (ANNOTATION_OVERRIDES[name]?.destructiveHint === true) return true;
  if (dryRunSet?.has(name)) return true;
  // An admin-scoped tool with no explicit override is destructive by default,
  // matching annotationsForScope("admin").
  return false;
}

// ---------------------------------------------------------------------------
// Central backfill
// ---------------------------------------------------------------------------

/**
 * Fill in missing annotation fields on every tool as it registers.
 *
 * WHY A WRAPPER RATHER THAN 112 EDITS
 * -----------------------------------
 * Before this, most tools declared `readOnlyHint` and nothing else: 102 of them
 * shipped without `openWorldHint`, even though every Carrier tool reaches an
 * external system. Hand-editing each registration would fix today's tools and
 * drift again with the next one, because nothing would hold the line.
 *
 * Wrapping `registerTool` once puts the scope registry — which is already the
 * source of truth for who may call what, and already CI-verified by
 * `verify-ask-registry.mjs` — in charge of the safety hints too. A new tool
 * gets correct annotations by virtue of having a scope.
 *
 * PRECEDENCE
 * ----------
 * Explicit beats derived, always. A registration that states a hint keeps it;
 * only absent fields are filled. So `annotationsFor(...)` at a call site (and
 * anything in ANNOTATION_OVERRIDES it already resolved) survives untouched, and
 * this wrapper is purely additive.
 *
 * Install BEFORE the first `registerTool` call, same as the scope filter.
 *
 * WHY THE SCOPE MAP IS A PARAMETER
 * --------------------------------
 * It would read better to import `ALL_TOOL_SCOPES` from `tool-registry.js`
 * directly, but that is a cycle: tool-registry imports the per-module scope
 * maps, and those modules import this file for `annotationsFor`. The cycle is
 * not benign — tool-registry builds `ALL_TOOL_SCOPES` at module-init by
 * spreading those maps, so it runs while they are still in their temporal dead
 * zone and throws `Cannot access 'STRIPE_CONNECT_TOOL_SCOPES' before
 * initialization`. That broke `generate-docs.mjs`'s scope lookup silently: it
 * caught the failure, defaulted every scope to `read`, and rewrote the
 * published docs claiming 121 read tools and 0 write.
 *
 * Taking the map as an argument keeps this module a leaf, so it cannot happen.
 */
export function installAnnotationDefaults(
  server: McpServer,
  scopes: Readonly<Record<string, ToolScope>>,
): { wrapped: boolean } {
  const target = server as unknown as {
    registerTool: (name: string, config: Record<string, unknown>, ...rest: unknown[]) => unknown;
  };
  const original = target.registerTool.bind(target);
  let wrapped = false;

  target.registerTool = (
    name: string,
    config: Record<string, unknown>,
    ...rest: unknown[]
  ) => {
    wrapped = true;

    // Capture the tool's own description so carrier_ask routes on capability
    // rather than on the tool name. Same interception point as the annotation
    // backfill — see @carrier/ocs-client router-catalog.ts.
    const description = config?.description;
    if (typeof description === "string") recordToolDescription(name, description);

    const scope = scopes[name] ?? UNKNOWN_TOOL_SCOPE;
    const declared = (config?.annotations ?? {}) as ToolAnnotations;
    const merged: ToolAnnotations = { ...annotationsFor(name, scope), ...declared };
    return original(name, { ...config, annotations: merged }, ...rest);
  };

  return {
    get wrapped() {
      return wrapped;
    },
  };
}

/**
 * Fallback for a tool the scope registry has not seen.
 *
 * An unknown name means the registry and the registrations have drifted, which
 * `verify-ask-registry.mjs` fails the build over. Treating it as `admin` means
 * that, in the window before that gate runs, the unknown tool is advertised as
 * the most dangerous thing it could be rather than the safest.
 */
const UNKNOWN_TOOL_SCOPE: ToolScope = "admin";
